import type { CoverPatch, Project, ProjectSettings } from '@/types';

/**
 * Projects service — 純 CRUD,5 endpoints。
 *
 * 規約:
 * - `brandKitId` — 純字串 optional;backend 不跨檢(UI 端應用 dropdown 從 brandKits.list() 限制)
 * - `brandKitId` / `projectId` — PATCH 不接受(想換重建一份)
 * - 軟刪除預設 hide;**Round 50 起砍掉前端 `includeDeleted` opt-in** — 若 admin /
 *   audit 需要看軟刪除資源應走獨立 admin service(未來開),前端使用者面 service 不
 *   暴露 admin scope 避免誤觸 + IDOR / privilege escalation 風險。
 *
 * @see docs/PROJECTS.md
 */

/** `POST /projects` — 建立時必填只有 `name`。 */
export interface ProjectCreateRequest {
  name: string;
  description?: string;
  brandKitId?: string;
  /** 缺欄位走後端預設(`aspect_ratios=["1:1","16:9","9:16"]` / `languages=["zh-TW"]`)。 */
  defaultSettings?: Partial<ProjectSettings>;
  /**
   * R245 — 封面。建立時可直接帶純色 / 不設(`CoverPatch` 型別本身就排除了 `image`)。
   *
   * ⚠️ **圖片封面建立時帶不了** —— `POST .../cover` 需要 pid,而 pid 是 create 的
   * 回傳值。UI 端要「先 create(帶色塊或不帶)→ 再 setCoverImage」兩步。
   */
  cover?: CoverPatch;
}

/**
 * `PATCH /projects/{id}` — 部分更新。
 *
 * 不接受 `brandKitId`(想換重建一份)/ `projectId`(path 隱含)/ `totalJobs` 等
 * 衍生欄位(後端 `extra="forbid"` 422)。
 */
export interface ProjectUpdateRequest {
  name?: string;
  description?: string | null;
  defaultSettings?: Partial<ProjectSettings>;
  /**
   * R245 — 設純色封面 / 清空(`{kind:'none'}` 連既有圖片一起清掉)。
   *
   * 圖片走 `setCoverImage()`;`CoverPatch` 沒有 `image` 這個 kind,所以「用 PATCH
   * 塞圖片」在編譯期就過不了(BE 那側是 422)。
   */
  cover?: CoverPatch;
  /**
   * R292.5(docs/107 追補;owner 2026-08-29 拍板 Q-15,推翻 v1「想換重建」)—
   * 品牌規範引用:`undefined` = 不改;**`''` = 清除引用**(wire 送空字串,不是 null ——
   * BE 語意 null=不改);非空 = 換引用(BE zero-trust 驗 kit 存在+未刪+同 group,
   * 缺失/跨群一律 404 `BRAND_KIT_NOT_FOUND`)。
   */
  brandKitId?: string;
}

/**
 * `PATCH /projects` 收不收 `brand_kit_id` 的 gate(R292.8;R213 起的 `*_SUPPORTED` 慣例)。
 *
 * ✅ **R397 起 true** —— 2026-10-07 重新取證(`git show origin/main:api-service/app/
 * projects/schemas.py`):BE main 的 `ProjectUpdateRequest` **已有** `brand_kit_id`
 * (docstring 載明「自 2026-08-29 起開放,Q-15;FE R292 #2 編輯專案資訊 dialog」;
 * 缺席/None = 不改、`''` = 清除、非空換引用 → 404 `BRAND_KIT_NOT_FOUND`)。R292.8
 * 當時 gate 關著是因為那批 code 還在 BE 交接分支 —— 它 8/29 就併回 main 了,gate
 * 一直沒人回頭翻(R351.8「藏了之後定期回頭驗」的又一例;owner 2026-10-07 回報
 * 「引用品牌規範應該能修正?」才抓到)。
 *
 * ⚠️ 要再關回 false 前先想清楚:關著時編輯 dialog 的品牌列退回唯讀顯示,而且
 * `extra="forbid"` 的語境下**絕不可半開**(夾帶送出 = 422 連名稱/說明一起存不進去)。
 */
export const PROJECT_BRAND_KIT_PATCH_SUPPORTED = true;

/** T12(docs/50)— `GET /projects` 排序欄位;BE FastAPI Literal 驗證,非法值 422。 */
export type ProjectSortField = 'created_at' | 'updated_at' | 'name';

/** 排序方向。 */
export type SortOrder = 'asc' | 'desc';

/** `list()` 排序選項;不帶 → BE 預設 `updated_at` / `desc`。 */
export interface ProjectListOptions {
  sortBy?: ProjectSortField;
  order?: SortOrder;
}

export interface ProjectsService {
  /**
   * 建立新的 project。
   *
   * @param req - 必填 `name`,其他選填
   * @returns 完整 `Project` 含 server 給的 `projectId` / 時間戳
   * @throws `ApiHttpError` 422 validation
   */
  create(req: ProjectCreateRequest): Promise<Project>;

  /**
   * 列本人未軟刪除的 project。
   *
   * @param opts - T12(docs/50)排序選項(`sort_by` / `order`);不帶走 BE 預設
   *   `updated_at` / `desc`。
   * @returns active project 列表,每筆含 `assetCount`(素材數)
   *
   * ⚠️ BE list 無分頁、cap 50(`updated_at desc` 取最近 50);當可見專案 >50 且
   *   `sortBy≠updated_at` 時排序只對「最近 50 筆」成立(docs/50 已知限制,MVP 無感)。
   */
  list(opts?: ProjectListOptions): Promise<Project[]>;

  /**
   * 取單一 project 詳情。
   *
   * @param projectId - 目標 ID
   * @returns 完整 `Project`
   * @throws `ApiHttpError` 404 `PROJECT_NOT_FOUND`
   *
   * **Round 50 起砍掉 `opts.includeDeleted`** — admin / audit 用獨立 service。
   */
  get(projectId: string): Promise<Project>;

  /**
   * 部分更新 project。
   *
   * @param projectId - 目標 ID
   * @param patch - 要更新的欄位;不寫的欄位保留原值
   * @returns 更新後完整 `Project`
   * @throws `ApiHttpError` 404 `PROJECT_NOT_FOUND` / 422 validation
   */
  update(projectId: string, patch: ProjectUpdateRequest): Promise<Project>;

  /**
   * 軟刪除 project。
   *
   * @param projectId - 目標 ID
   * @returns 刪除後完整 entity,`deletedDatetime` 有實值
   * @throws `ApiHttpError` 404 `PROJECT_NOT_FOUND`
   */
  remove(projectId: string): Promise<Project>;

  /**
   * R245 — 上傳封面圖(`POST /projects/{pid}/cover`,multipart 欄位名 `file`)。
   *
   * **一次呼叫就完成**:沒有 intent / finalize、不簽 URL、不自己 PUT 到 GCS。
   * 後端把原圖縮成長邊 480 的 WebP data URI 直接寫進 doc,回傳完整 `Project`
   * (`cover.kind === 'image'`)→ 呼叫端**不需要再 GET 一次**。
   *
   * 限制:≤5 MB / PNG·JPEG·WebP(看 magic byte)/ 10 次每分鐘。
   *
   * ⚠️ **失敗不會改到任何資料** —— 封面沒有中間狀態,任一閘門不過就是 raise,
   * 原封面原封不動。呼叫端只要顯示錯誤、保留畫面上的舊封面,**不需要回滾、
   * 不需要重試補償、不需要清理**。
   *
   * @throws `ApiHttpError` 413 `COVER_SIZE_EXCEEDS_LIMIT` / 400 `COVER_MIME_NOT_ALLOWED`
   *   / 413 `COVER_DIMENSIONS_EXCEED_LIMIT` / 400 `COVER_DECODE_FAILED`
   *   / 413 `COVER_COMPRESS_FAILED` / 404 `PROJECT_NOT_FOUND` / 429 限流
   */
  setCoverImage(projectId: string, file: File): Promise<Project>;
}
